<template>
  <div class="app" :class="{ eingeklappt, modus: imModus }">

    <!-- Erstes Element der Seite: wer mit der Tastatur kommt, muss nicht
         durch die ganze Leiste. Unsichtbar, bis er den Fokus hat. -->
    <a href="#inhalt" class="sprunglink" @click.prevent="zumInhalt">Zum Inhalt springen</a>

    <!-- Leiste links: alle Bereiche auf einen Blick, auf jeder Seite gleich.
         Laesst sich auf Icon-Breite einklappen (Knopf unten, Strg+B), nur
         bis zum Neuladen.
         Auf schmalen Bildschirmen wird sie zur Zeile oben. -->
    <!-- Im Uebungsmodus (laufende Sprechregeln-Uebung) ist die Leiste weg:
         die Uebung hat den ganzen Bildschirm, «Beenden» fuehrt hinaus. -->
    <nav v-show="!imModus" class="leiste" aria-label="Hauptnavigation">
      <RouterLink to="/" class="wortmarke" data-tipp="Startseite">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9 2.5l1.5 5.5" />
          <rect x="6" y="8" width="12" height="13.5" rx="1.5" />
          <path d="M9 12.5h6M9 15.5h6M9 18.5h3" />
        </svg>
        <span class="leiste__text">Funktrainer</span>
      </RouterLink>

      <div class="leiste__liste">
        <template v-for="g in gruppen" :key="g.titel ?? 'start'">
          <p v-if="g.titel" class="leiste__gruppe">
            <span class="leiste__text">{{ g.titel }}</span>
          </p>
          <RouterLink v-for="e in g.eintraege" :key="e.label" :to="e.to" :data-tipp="e.label">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="e.icon" /></svg>
            <span class="leiste__text">{{ e.label }}</span>
          </RouterLink>
        </template>
      </div>

      <!-- In einer Sitzung: der eigene Name, damit der Ausbildner ihn zuordnen kann. -->
      <RouterLink v-if="schuelerSitzung.daten" to="/sitzung" class="sitzungsname"
                  :data-tipp="'In der Sitzung als ' + schuelerSitzung.daten.name">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="icons.person" /></svg>
        <span class="leiste__text">
          <span class="sitzungsname__etikett">In der Sitzung als</span>
          {{ schuelerSitzung.daten.name }}
        </span>
      </RouterLink>

      <button class="einklappen" :aria-expanded="!eingeklappt" @click="umschalten"
              :data-tipp="eingeklappt ? 'Leiste ausklappen (Strg+B)' : null">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="icons.einklappen" /></svg>
        <span class="leiste__text">Einklappen <kbd>Strg+B</kbd></span>
      </button>
    </nav>

    <!-- key = Routenname: /sprechregeln und /sitzung/sprechregeln zeigen dieselbe
         Komponente. Ohne key wuerde Vue sie beim Wechsel wiederverwenden, und
         setup() — wo die Sitzung geladen wird — liefe nicht neu. -->
    <main id="inhalt" class="haupt" tabindex="-1">
      <router-view v-slot="{ Component, route }">
        <component :is="Component" :key="route.name"/>
      </router-view>
    </main>

  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { schuelerSitzung } from '@/logic/sitzungSchueler'
import { modes } from '@/data/Modes'

// Icons der Leiste: schlichte Striche auf 24er-Raster, ein Pfad pro Icon.
// Nur fuer die Leiste; die Geraete-Icons stehen in data/Icons.js.
const icons = {
  uebersicht: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  kennenlernen: 'M10.5 4a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM15.5 15.5L20 20',
  lernen: 'M4 6h2M4 12h2M4 18h2M9 6h11M9 12h11M9 18h11',
  anfaenger: 'M4 4h16v16H4zM8 12.5l2.5 2.5L16 9',
  sprechregeln: 'M4 5h16v11H10l-6 4z',
  beitreten: 'M14 4h6v16h-6M3 12h11M10 8l4 4-4 4',
  leiten: 'M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM3 20c0-3.3 2.7-5 6-5s6 1.7 6 5M16 5.5a3 3 0 0 1 0 5.5M17.5 15c2 .5 3.5 2 3.5 5',
  person: 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c0-4 3.6-6 8-6s8 2 8 6',
  einklappen: 'M4 4v16M15 7l-5 5 5 5M10 12h10'
}

export default {
  setup() {
    // Der Sitzungsmodus hat keine eigene Seite in der Leiste: er ist nur
    // ueber das Beitreten erreichbar.
    const modusIcon = { kennenlernen: icons.kennenlernen, lernen: icons.lernen, anfaenger: icons.anfaenger }
    const gruppen = [
      { titel: null, eintraege: [{ label: 'Übersicht', to: '/training', icon: icons.uebersicht }] },
      { titel: 'Funkgerät', eintraege: modes.filter(m => !m.features.sitzung).map(m => ({
          label: m.label, to: { name: 'Funkgeraet', params: { mode: m.id } }, icon: modusIcon[m.id] ?? icons.anfaenger })) },
      { titel: 'Sprechregeln', eintraege: [{ label: 'Übungen', to: '/sprechregeln', icon: icons.sprechregeln }] },
      { titel: 'Unterricht', eintraege: [
          { label: 'Sitzung beitreten', to: { name: 'SitzungBeitreten' }, icon: icons.beitreten },
          { label: 'Sitzung leiten', to: { name: 'SitzungLeiten' }, icon: icons.leiten }] }
    ]

    // Eingeklappt gilt nur bis zum Neuladen: nichts wird im Browser
    // gespeichert (PRODUCT.md, «No persistence at all»).
    const eingeklappt = ref(false)
    const umschalten = () => { eingeklappt.value = !eingeklappt.value }

    // Uebungsmodus: eine Sprechregeln-Uebung laeuft (Art und Kategorie
    // gewaehlt) oder die Sitzung zeigt ihre Uebungen.
    const route = useRoute()
    const imModus = computed(() =>
      (route.name === 'Sprechregeln' && !!route.query.art && !!route.query.kategorie)
      || route.name === 'SitzungSprechregeln')

    // Sprunglink: Fokus direkt auf den Inhalt, an der Leiste vorbei.
    const zumInhalt = () => document.getElementById('inhalt')?.focus()

    // Strg+B (auf dem Mac Cmd+B), wie bei der shadcn-Sidebar.
    const taste = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault()
        umschalten()
      }
    }
    onMounted(() => window.addEventListener('keydown', taste))
    onUnmounted(() => window.removeEventListener('keydown', taste))

    return { schuelerSitzung, gruppen, icons, eingeklappt, umschalten, zumInhalt, imModus }
  }
}
</script>

<style>
/* Archivo, selbst gehostet. Kein Aufruf an Google: die App soll im
   Kurslokal ohne Netzzugriff auf Dritte funktionieren.
   Herkunft und Lizenz: src/assets/fonts/HERKUNFT.txt */
@font-face {
  font-family: 'Archivo';
  src: url('~@/assets/fonts/archivo-var-latin.woff2') format('woff2');
  font-weight: 400 800;
  font-style: normal;
  font-display: swap;
}

/* Project palette — the only place these hex values should appear.
   The simulated device (chassis, display, keypad) keeps its own realistic
   hardware colors and does not use these. */
:root {
  --zs-blau: #0033A0;
  --zs-orange: #FF8200;
  --zs-oliv: #556B2F;
  --zs-orange-dunkel: #C25E00;
  --zs-blau-hell: #E8EDF7;
  --zs-oliv-hell: #EAEDDF;
  --zs-anthrazit: #1F2328;
  --zs-steingrau: #6B7078;
  --zs-warmweiss: #F7F6F2;
  --zs-warnrot: #B3261E;

  /* Richtung «Klartext» (2026-10-18): hell und ruhig. Die Palette oben
     bleibt wie sie war — das Ablaufgitter benutzt sie und soll
     unveraendert bleiben. Neu: Flaechen, Linien, Rundung und Schatten
     der Oberflaeche. Blau = anklickbar, Orange = aktuell / Fokus,
     Oliv = richtig, Warnrot = falsch. */
  --zs-flaeche: #F3F5F8;
  --zs-tafel: #FFFFFF;
  --zs-tinte: #131A26;
  --zs-grau: #586172;          /* Nebentext; 6.2:1 auf Weiss, 5.7:1 auf der Flaeche */
  --zs-rahmen: #DFE3EA;
  --zs-leiste: #0B1F4D;        /* Dunkelblau fuer kleine Marken (Stufennummer) */
  --zs-leiste-text: #C9D3E8;
  --zs-blau-dunkel: #00287E;   /* Hauptknopf beim Darueberfahren */
  --zs-blau-mittel: #D6E0F3;   /* Hellblau beim Darueberfahren */
  --zs-orange-hell: #FFF1E3;   /* «ausgewaehlt»-Flaeche */
  --zs-warnrot-hell: #FBEAE8;
  --zs-ecke: 8px;
  --zs-ecke-gross: 14px;
  --zs-schatten: 0 1px 2px rgba(16, 24, 40, 0.05), 0 2px 8px rgba(16, 24, 40, 0.05);
  --zs-schatten-hoch: 0 2px 4px rgba(16, 24, 40, 0.06), 0 12px 28px -8px rgba(16, 24, 40, 0.16);
  --zs-leistenbreite: 15rem;

  --zs-linie: rgba(31, 35, 40, 0.14);
  /* Funktext (Sprueche, Bausteine) in fester Laufweite, Systemschrift:
     muss im Kurslokal ohne Fremdnetz laufen. */
  --zs-funkschrift: ui-monospace, 'Cascadia Mono', Consolas, 'Liberation Mono', monospace;
  /* Keine Kopfzeile mehr ueber den Seiten (die Leiste steht links). Bleibt
     als Variable, weil die Funkgeraet-Spalte damit ihren Klebeabstand
     rechnet. */
  --zs-kopfhoehe: 0px;
}

/* Sichtbar nur fuer Screenreader. */
.nur-vorlesen {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

body {
  margin: 0;
  background: var(--zs-flaeche);
}

#app {
  font-family: 'Archivo', system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: var(--zs-tinte);
}

/* Flächen, die der Browser sonst selbst einfärbt — sie gehören zur
   Gestaltung wie alles andere. */
::selection {
  background: var(--zs-orange);
  color: var(--zs-anthrazit);
}

body {
  caret-color: var(--zs-blau);
  scrollbar-color: var(--zs-grau) var(--zs-flaeche);
  scrollbar-width: thin;
}

:focus-visible {
  outline: 2px solid var(--zs-blau);
  outline-offset: 3px;
}

/* ─── Geruest: Leiste links, Inhalt rechts ────────────────── */

.app {
  display: grid;
  grid-template-columns: var(--zs-leistenbreite) minmax(0, 1fr);
  min-height: 100vh;
}

.haupt {
  min-width: 0;
}

.haupt:focus {
  outline: none;
}

/* Sprunglink: ausserhalb des Rasters, erscheint oben links beim Fokus. */
.sprunglink {
  position: fixed;
  top: 0.5rem;
  left: 0.5rem;
  z-index: 200;
  padding: 0.6rem 1rem;
  font-weight: 650;
  color: var(--zs-tinte);
  text-decoration: none;
  background: var(--zs-orange);
  border-radius: var(--zs-ecke);
  transform: translateY(-200%);
}

.sprunglink:focus {
  transform: none;
  outline: 3px solid var(--zs-tinte);
  outline-offset: 2px;
}

/* Klebt am linken Rand und ist nie hoeher als der Bildschirm. Hell, damit
   der Inhalt das Gewicht hat; z-index unter der Abdunklung von Rundgang
   und Erklaerung (100). */
.leiste {
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  height: 100vh;
  padding: 1.25rem 0.85rem 1rem;
  overflow-y: auto;
  box-sizing: border-box;
  text-align: left;
  color: var(--zs-grau);
  background: var(--zs-tafel);
  border-right: 1px solid var(--zs-rahmen);
}

.wortmarke {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.25rem 0.6rem;
  font-size: 1.15rem;
  font-weight: 750;
  letter-spacing: -0.015em;
  color: var(--zs-tinte);
  text-decoration: none;
}

.leiste__liste {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.15rem;
}

.leiste__gruppe {
  margin: 1.1rem 0.7rem 0.35rem;
  font-size: 0.72rem;
  font-weight: 650;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8A92A3;
}

.leiste__liste a {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 2.5rem;
  padding: 0.45rem 0.7rem;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--zs-tinte);
  text-decoration: none;
  border-radius: var(--zs-ecke);
  box-sizing: border-box;
}

.leiste__liste a svg {
  color: var(--zs-grau);
}

.leiste__liste a:hover {
  background: var(--zs-flaeche);
}

/* Die aktive Seite: hellblaue Flaeche, blaue Schrift UND ein oranger
   Punkt rechts — nicht nur eine andere Farbe. RouterLink setzt
   aria-current selbst. */
.leiste__liste a[aria-current="page"] {
  font-weight: 650;
  color: var(--zs-blau);
  background: var(--zs-blau-hell);
}

.leiste__liste a[aria-current="page"] svg {
  color: var(--zs-blau);
}

.leiste__liste a[aria-current="page"]::before {
  content: '';
  order: 2;
  width: 6px;
  height: 6px;
  margin-left: auto;
  background: var(--zs-orange);
  border-radius: 50%;
}

.sitzungsname {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.65rem 0.75rem;
  font-weight: 700;
  color: var(--zs-blau);
  text-decoration: none;
  background: var(--zs-blau-hell);
  border-radius: var(--zs-ecke);
}

.sitzungsname .leiste__text {
  display: flex;
  flex-direction: column;
}

.sitzungsname__etikett {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--zs-grau);
}

.leiste :focus-visible {
  outline: 3px solid var(--zs-orange);
  outline-offset: 0;
}

/* Icons der Leiste: Striche in der Textfarbe. */
.leiste svg {
  flex: none;
  width: 1.2rem;
  height: 1.2rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.wortmarke svg {
  width: 1.4rem;
  height: 1.4rem;
  stroke: var(--zs-orange);
}

.sitzungsname svg {
  stroke: var(--zs-blau);
}

/* Einklappen: ganz unten, sieht aus wie ein Eintrag. */
.einklappen {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  width: 100%;
  padding: 0.5rem 0.6rem;
  font: inherit;
  font-size: 0.9rem;
  text-align: left;
  color: var(--zs-grau);
  background: transparent;
  border: none;
  border-radius: var(--zs-ecke);
  cursor: pointer;
}

.einklappen:hover {
  color: var(--zs-tinte);
  background: var(--zs-flaeche);
}

.einklappen .leiste__text {
  white-space: nowrap;
}

.einklappen kbd {
  margin-left: 0.35rem;
  padding: 0 0.3rem;
  font-family: var(--zs-funkschrift);
  font-size: 0.7rem;
  border: 1px solid var(--zs-rahmen);
  border-radius: 4px;
}

.einklappen svg {
  transition: transform 0.2s;
}

/* ─── Eingeklappt: nur Icons ─────────────────────────────────
   Die Texte bleiben fuer Screenreader da (wie .nur-vorlesen), die
   Gruppentitel werden zu Trennlinien, beim Darueberfahren oder per
   Tastaturfokus erscheint der Name als Tooltip rechts. */

.app {
  transition: grid-template-columns 0.2s ease-out;
}

.app.eingeklappt {
  grid-template-columns: 4.5rem minmax(0, 1fr);
}

/* Uebungsmodus: nur der Inhalt, ueber die ganze Breite. */
.app.modus {
  grid-template-columns: minmax(0, 1fr);
}

.eingeklappt .leiste {
  padding-inline: 0.55rem;
  overflow: visible;
}

.eingeklappt .leiste__text {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.eingeklappt .wortmarke,
.eingeklappt .leiste__liste a,
.eingeklappt .einklappen,
.eingeklappt .sitzungsname {
  justify-content: center;
  padding-inline: 0;
}

.eingeklappt .leiste__liste a[aria-current="page"]::before {
  display: none;
}

.eingeklappt .leiste__gruppe {
  height: 1px;
  margin: 0.6rem 0.5rem;
  background: var(--zs-rahmen);
}

.eingeklappt .einklappen svg {
  transform: rotate(180deg);
}

.eingeklappt [data-tipp] {
  position: relative;
}

.eingeklappt [data-tipp]::after {
  content: attr(data-tipp);
  position: absolute;
  top: 50%;
  left: calc(100% + 0.75rem);
  z-index: 60;
  padding: 0.3rem 0.6rem;
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
  color: #ffffff;
  background: var(--zs-tinte);
  border-radius: var(--zs-ecke);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  opacity: 0;
  transform: translate(-4px, -50%);
  pointer-events: none;
  transition: opacity 0.12s, transform 0.12s;
}

.eingeklappt [data-tipp]:hover::after,
.eingeklappt [data-tipp]:focus-visible::after {
  opacity: 1;
  transform: translate(0, -50%);
}

@media (prefers-reduced-motion: reduce) {
  .app,
  .einklappen svg,
  .eingeklappt [data-tipp]::after {
    transition: none;
  }
}

/* Schmal: die Leiste wird zur Zeile oben, die Bereiche laufen seitlich
   in ihrer eigenen Zeile (nur sie scrollt, nie die Seite). */
@media (max-width: 900px) {
  .app {
    grid-template-columns: minmax(0, 1fr);
  }

  .leiste {
    position: static;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 1rem;
    height: auto;
    padding: 0.6rem 16px;
    overflow: visible;
  }

  .leiste__liste {
    flex-direction: row;
    flex: 1 1 100%;
    order: 3;
    gap: 0.25rem;
    overflow-x: auto;
  }

  .leiste__liste a {
    flex: none;
  }

  .leiste__gruppe {
    display: none;
  }

  .sitzungsname {
    margin: 0 0 0 auto;
    padding: 0.3rem 0.6rem;
  }

  /* Einklappen gibt es hier nicht: die Zeile ist schon schmal. Ein
     gemerktes «eingeklappt» wird ignoriert. */
  .einklappen {
    display: none;
  }

  .app.eingeklappt {
    grid-template-columns: minmax(0, 1fr);
  }

  .eingeklappt .leiste {
    padding-inline: 16px;
  }

  .eingeklappt .leiste__text {
    position: static;
    width: auto;
    height: auto;
    overflow: visible;
    clip: auto;
  }

  .eingeklappt .leiste__liste a {
    padding-inline: 0.6rem;
  }

  .eingeklappt [data-tipp]::after {
    display: none;
  }
}
</style>
